import { CaretDown, CaretLeft, CaretRight, Link, Plus } from '@phosphor-icons/react';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  IconButton,
  Inline,
  Stack,
} from '@reelplicate/ui';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import type { Account, AccountGroup } from './calendar-model';
import { PlatformDot } from './platform-stack';
import { PLATFORM_NAMES } from './platforms';

type AccountsPanelProps = {
  groups: AccountGroup[];
  accounts: Account[];
  /** Accounts whose posts show on the calendar. */
  visible: Set<string>;
  onToggleAccount: (id: string, visible: boolean) => void;
  onCollapse: () => void;
  onCreatePost: () => void;
};

/**
 * Figma "Accounts" (143:968): the calendar's left panel. Connected accounts
 * by brand / persona, each with a checkbox that shows or hides its posts.
 */
export function AccountsPanel({
  groups,
  accounts,
  visible,
  onToggleAccount,
  onCollapse,
  onCreatePost,
}: AccountsPanelProps) {
  const platformCount = new Set(accounts.map((a) => a.platform)).size;
  return (
    <Stack
      as="aside"
      aria-label="Accounts"
      gap={4}
      className="bg-bg-surface border-border-subtle h-full w-[280px] shrink-0 overflow-y-auto border-r px-4 py-6"
    >
      <Inline gap={2}>
        <h2 className="text-heading-h4 text-text-primary flex-1">Accounts</h2>
        <IconButton
          icon={CaretLeft}
          size="sm"
          aria-label="Hide accounts"
          onClick={onCollapse}
        />
      </Inline>
      <Button leadingIcon={Plus} onClick={onCreatePost}>
        Create post
      </Button>
      <Button asChild variant="outline" leadingIcon={Link}>
        <RouterLink to="/settings/connected-accounts">Connect account</RouterLink>
      </Button>
      {accounts.length === 0 ? (
        <p className="text-body-sm text-text-secondary">
          No accounts yet. Add your TikTok, Instagram and YouTube accounts to plan posts
          for them.
        </p>
      ) : (
        groups.map((group) => (
          <AccountGroupSection
            key={group.id}
            group={group}
            accounts={accounts.filter((a) => a.groupId === group.id)}
            visible={visible}
            onToggleAccount={onToggleAccount}
          />
        ))
      )}
      <p className="text-caption text-text-tertiary mt-auto">
        {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'} ·{' '}
        {platformCount} {platformCount === 1 ? 'platform' : 'platforms'}
      </p>
    </Stack>
  );
}

function AccountGroupSection({
  group,
  accounts,
  visible,
  onToggleAccount,
}: {
  group: AccountGroup;
  accounts: Account[];
  visible: Set<string>;
  onToggleAccount: (id: string, visible: boolean) => void;
}) {
  const [open, setOpen] = useState(true);
  const listId = useId();
  const Caret = open ? CaretDown : CaretRight;
  return (
    <Stack gap={0.5}>
      <Inline gap={1.5} className="py-1.5">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
          className="text-label-md text-text-primary focus-visible:shadow-focus-ring inline-flex items-center gap-1.5 rounded-xs outline-none"
        >
          <Caret aria-hidden className="text-icon-secondary size-3.5" />
          {group.name}
        </button>
        <Badge size="sm" tone={group.kind === 'brand' ? 'brand' : 'neutral'}>
          {group.kind === 'brand' ? 'Brand' : 'Persona'}
        </Badge>
      </Inline>
      {open && (
        <Stack as="ul" id={listId} gap={0.5} aria-label={`${group.name} accounts`}>
          {accounts.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              checked={visible.has(account.id)}
              onCheckedChange={(checked) => onToggleAccount(account.id, checked)}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

/** Figma "Calendar / Account row" (178:19908). */
function AccountRow({
  account,
  checked,
  onCheckedChange,
}: {
  account: Account;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const platform = PLATFORM_NAMES[account.platform];
  return (
    <Inline
      as="li"
      className="gap-2.5 rounded-md py-1.5 pr-1 pl-2"
      title={`${account.handle} · ${platform}`}
    >
      <span className="relative shrink-0">
        <Avatar
          size={32}
          src={account.avatarUrl ?? undefined}
          initials={account.initials}
          alt=""
        />
        <PlatformDot
          platform={account.platform}
          size="sm"
          className="absolute top-5 left-5"
        />
      </span>
      <span className="text-body-sm-strong text-text-primary min-w-0 flex-1 truncate">
        {account.handle}
      </span>
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        aria-label={`Show ${account.handle} (${platform}) on the calendar`}
      />
    </Inline>
  );
}
